import { isValidFirebaseEmail } from '../../../../pure-js/libs/emailValidation'

/**
 * Centralized Email Validation Helper
 *
 * This helper provides consistent email validation across the entire application.
 * All email validation logic should use these functions to ensure consistency.
 */

/**
 * Validates if an email address is accepted by Firebase Auth
 * (which is stricter than basic RFC validation, e.g. requires real TLDs)
 */
export const isValidEmail = (email: string): boolean => {
  if (!email || email.length === 0) return false

  const trimmedEmail = email.trim()
  return trimmedEmail.length > 0 && isValidFirebaseEmail(trimmedEmail)
}

/**
 * Validates email with additional checks for common issues
 *
 * @param email - The email address to validate
 * @returns object with validation result and error message
 */
export const validateEmailWithDetails = (email: string): { isValid: boolean; error?: string } => {
  if (!email || email.trim().length === 0) {
    return { isValid: false, error: 'Email is required' }
  }

  if (email.length < 3) {
    return { isValid: false, error: 'Email is too short' }
  }

  if (!isValidEmail(email)) {
    return { isValid: false, error: 'Please enter a valid email address' }
  }

  return { isValid: true }
}

/**
 * Email validation for form validation with translation support
 *
 * @param email - The email address to validate
 * @param t - Translation function
 * @returns object with validation result and translated error message
 */
export const validateEmailForForm = (
  email: string,
  t: (key: string) => string
): { isValid: boolean; error?: string } => {
  if (!email || email.trim().length === 0) {
    return { isValid: false, error: t('validation.emailRequired') }
  }

  if (email.length < 3) {
    return { isValid: false, error: t('validation.emailTooShort') }
  }

  if (!isValidEmail(email)) {
    return { isValid: false, error: t('validation.emailInvalid') }
  }

  return { isValid: true }
}
